import { Component, OnInit, TemplateRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import {
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
  FormArray
} from '@angular/forms';
import { AdminService } from '../../services/admin/admin.service';
import { HelpersService } from '../../../shared/servicio/helpers/helpers.service';
import { Router } from '@angular/router';

interface CreateSurveyRequest {
  title: string;
  description: string;
  all_medics: boolean;
  recipients_specialties: Array<{
    id: number;
    name: string;
  }>;
}

@Component({
  selector: 'app-create-encuesta',
  templateUrl: './create-encuesta.component.html',
  styleUrls: ['./create-encuesta.component.css']
})
export class CreateEncuestaComponent implements OnInit, OnDestroy {
  @Input() modalReference: BsModalRef;

  // Configuraciones de modales
  modalRef: BsModalRef;
  modalRef2: BsModalRef;
  modalRef3: BsModalRef;

  config = {
    class: 'modal-dialog-centered modal-app',
    backdrop: true,
    ignoreBackdropClick: true
  };

  config2 = {
    class: 'modal-dialog-centered modal-app',
    backdrop: true,
    ignoreBackdropClick: false
  };

  config3 = {
    class: 'modal-dialog-centered modal-app',
    backdrop: true,
    ignoreBackdropClick: true
  };

  // Formulario y controles
  formsurveysAdd: FormGroup;
  title: FormControl;
  description: FormControl;
  select: FormControl;

  // Estados y datos
  submitted: boolean = false;
  preloader: boolean = false;
  div_error: number = 0;
  div_text_error: string = '';

  // Especialidades
  specialties: any[] = [];
  search_specialties: any[] = [];
  select_specialties: any[] = [];
  search_specilties_text: string = '';
  error_selected: string = '';

  // Encuesta creada
  name_surveys: string = '';
  id_survey: number = 0;

  // Configuración de API
  useNewApi: boolean = true;

  // Control de creación
  isCreating: boolean = false;

  constructor(
    private modalService: BsModalService,
    private formBuilder: FormBuilder,
    public admin: AdminService,
    public _helper: HelpersService
  ) { }

  ngOnInit() {
    this.initializeComponent();
    this.loadSpecialties();
    this.setupForm();
  }

  ngOnDestroy() {
    // Cerrar todos los modales abiertos
    if (this.modalRef) this.modalRef.hide();
    if (this.modalRef2) this.modalRef2.hide();
    if (this.modalRef3) this.modalRef3.hide();
  }
  @ViewChild('especialidades') especialidades: TemplateRef<any>;

  /**
   * Inicializa el componente
   */
  private initializeComponent() {
    this.submitted = false;
    this.id_survey = 0;
    this.div_error = 0;
    this.div_text_error = '';
    this.preloader = false;
    this.isCreating = false;
    this.select_specialties = [];
    this.search_specialties = [];
    this.search_specilties_text = '';
    this.error_selected = '';
  }

  /**
   * Configura el formulario
   */
  private setupForm() {
    this.formsurveysAdd = this.formBuilder.group({
      title: [
        '',
        [
          Validators.required,
          Validators.minLength(5),
          Validators.maxLength(150),
          Validators.pattern('^[a-zA-ZáéíóúÁÉÍÓÚÑñ0-9 .,;:!?¡¿()\\-]+$')
        ]
      ],
      description: [
        '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(500)
        ]
      ],
      select: [0, [Validators.required, Validators.min(1)]]
    });
  }

  /**
   * Carga las especialidades
   */
  loadSpecialties() {
    this.admin.specialties_list().subscribe({
      next: (data: any) => {
        this.specialties = Array.isArray(data) ? data : [];
        console.log('Especialidades cargadas:', this.specialties.length);
      },
      error: (error) => {
        console.error('Error cargando especialidades:', error);
        this.specialties = [];
        this._helper.modalAlert('Error al cargar las especialidades');
      }
    });
  }

  /**
   * Getter para acceder fácilmente a los controles del formulario
   */
  get shortSurveysForm() {
    return this.formsurveysAdd.controls;
  }

  /**
   * Cierra el modal principal
   */
  hideCrear() {
    if (this.modalReference) {
      this.modalReference.hide();
    }
  }

  /**
   * Crea una nueva encuesta
   */
  surveysAdd(template: TemplateRef<any>) {
    // Evitar doble submit
    if (this.isCreating || this.preloader) {
      console.log('Creación ya en proceso');
      return;
    }

    console.log('Iniciando creación de encuesta');
    this.isCreating = true;
    this.preloader = true;
    this.submitted = true;
    this.div_error = 0;
    this.div_text_error = '';

    // Validar formulario básico
    if (!this.formsurveysAdd.valid) {
      console.log('Formulario inválido:', this.getFormErrors());
      this.showError('Por favor complete todos los campos requeridos correctamente');
      this.stopCreating();
      return;
    }

    // Validar especialidades si se seleccionó opción específica
    if (this.shortSurveysForm.select.value === 2 && this.select_specialties.length === 0) {
      console.log('No se han seleccionado especialidades');
      this.showError('Debe seleccionar al menos una especialidad');
      this.stopCreating();
      return;
    }

    // Preparar datos y crear encuesta
    const surveyData = this.prepareSurveyData();
    this.createSurvey(surveyData, template);
  }

  /**
   * Obtiene los errores del formulario para debugging
   */
  private getFormErrors(): any {
    return {
      title: this.shortSurveysForm.title.errors,
      description: this.shortSurveysForm.description.errors,
      select: this.shortSurveysForm.select.errors,
      formValid: this.formsurveysAdd.valid,
      formValue: this.formsurveysAdd.value
    };
  }

  /**
   * Muestra un error y detiene la creación
   */
  private showError(message: string) {
    this.div_error = 1;
    this.div_text_error = message;
  }

  /**
   * Detiene el proceso de creación
   */
  private stopCreating() {
    this.preloader = false;
    this.isCreating = false;
  }

  /**
   * Prepara los datos para crear la encuesta
   */
  private prepareSurveyData(): CreateSurveyRequest {
    const isAllMedics = this.shortSurveysForm.select.value === 1;

    const surveyData: CreateSurveyRequest = {
      title: this.shortSurveysForm.title.value.trim(),
      description: this.shortSurveysForm.description.value.trim(),
      all_medics: isAllMedics,
      recipients_specialties: isAllMedics ? [] : this.select_specialties.slice()
    };

    console.log('Datos preparados para encuesta:', surveyData);
    return surveyData;
  }

  /**
   * Crea la encuesta usando el servicio
   */
  private createSurvey(surveyData: CreateSurveyRequest, template: TemplateRef<any>) {
    const createMethod = this.useNewApi ?
      this.admin.createSurveyNew(surveyData) :
      this.admin.create_survey(
        surveyData.title,
        surveyData.description,
        surveyData.all_medics,
        surveyData.recipients_specialties
      );

    createMethod.subscribe({
      next: (data) => {
        console.log('Encuesta creada exitosamente:', data);
        this.handleSurveyCreated(data, template);
      },
      error: (error) => {
        console.error('Error al crear encuesta:', error);
        this.handleSurveyError(error);
      }
    });
  }

  /**
   * Maneja la respuesta exitosa de creación de encuesta
   */
  private handleSurveyCreated(data: any, template: TemplateRef<any>) {
    this.preloader = false;
    this.isCreating = false;
    this.div_error = 0;

    // Extraer ID de la encuesta creada
    this.id_survey = data.survey_id || data.id || data.body.id;
    this.name_surveys = this.shortSurveysForm.title.value;

    console.log('ID de encuesta creada:', this.id_survey);

    if (!this.id_survey) {
      console.error('No se pudo obtener el ID de la encuesta creada');
      this.showError('Error: No se pudo obtener el ID de la encuesta');
      return;
    }

    // Resetear formulario
    this.resetForm();

    // Cerrar modal actual y abrir modal de preguntas
    this.hideCrear();

    // Delay pequeño para asegurar que el modal se cierre antes de abrir el nuevo
    setTimeout(() => {
      this.modalRef3 = this.modalService.show(template, this.config3);
      this._helper.modalSuccess('¡Encuesta creada exitosamente!');
    }, 200);
  }

  /**
   * Maneja errores en la creación de encuesta
   */
  private handleSurveyError(error: any) {
    console.error('Error creating survey:', error);
    this.preloader = false;
    this.isCreating = false;

    // Extraer mensaje de error
    let errorMessage = 'Ocurrió un error al crear la encuesta';

    if (error.error.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    this.div_error = 1;
    this.div_text_error = errorMessage;
    this._helper.modalAlert('¡' + errorMessage + '!');
  }

  /**
   * Maneja la selección del tipo de especialidad
   */
  typeSelect() {
    const value = this.shortSurveysForm.select.value;

    console.log('Tipo seleccionado:', value);

    if (value === 2) {
      // Limpiar búsqueda previa
      this.search_specilties_text = '';
      this.search_specialties = [];
      this.error_selected = '';

      // Abrir modal de especialidades
      this.modalRef2 = this.modalService.show(this.especialidades, this.config2);
    } else if (value === 1) {
      // Limpiar especialidades seleccionadas si se elige "todas"
      this.select_specialties = [];
    }
  }
  /**
   * Filtra las especialidades por texto de búsqueda
   */
  specialties_array() {
    this.search_specialties = [];
    this.error_selected = '';

    const searchText = this.search_specilties_text.trim();

    if (!searchText || searchText.length === 0) {
      return;
    }

    if (searchText.length < 2) {
      return;
    }

    const searchLower = searchText.toLowerCase();

    this.search_specialties = this.specialties.filter(specialty =>
      specialty.name && specialty.name.toLowerCase().includes(searchLower)
    );

    console.log(`Encontradas ${this.search_specialties.length} especialidades para "${searchText}"`);
  }

  /**
   * Selecciona una especialidad
   */
  selected_specialties(id: number, name: string) {
    // Validar que no esté ya seleccionada
    if (this.isSpecialtySelected(id)) {
      this.error_selected = 'La especialidad ya está seleccionada.';
      setTimeout(() => {
        this.error_selected = '';
      }, 3000);
      return;
    }

    // Agregar especialidad
    this.select_specialties.push({ id, name });
    this.error_selected = '';

    console.log('Especialidad agregada:', { id, name });
    console.log('Total especialidades seleccionadas:', this.select_specialties.length);

    // Limpiar búsqueda
    this.search_specilties_text = '';
    this.search_specialties = [];
  }

  /**
   * Verifica si una especialidad ya está seleccionada
   */
  private isSpecialtySelected(id: number): boolean {
    return this.select_specialties.some(specialty => specialty.id === id);
  }

  /**
   * Elimina una especialidad seleccionada
   */
  delete_selected(array: any[], index: number) {
    if (index >= 0 && index < array.length) {
      const removed = array.splice(index, 1);
      console.log('Especialidad eliminada:', removed[0]);
      console.log('Especialidades restantes:', array.length);
    }
  }

  /**
   * Alterna entre nueva API y API legacy
   */
  toggleApiMode() {
    this.useNewApi = !this.useNewApi;
    console.log('Modo API cambiado a:', this.useNewApi ? 'nueva' : 'legacy');
  }

  /**
   * Obtiene información de la encuesta creada
   */
  getSurveyInfo() {
    return {
      id: this.id_survey,
      title: this.name_surveys
    };
  }

  /**
   * Valida si el formulario está completo y correcto
   */
  isFormValid(): boolean {
    if (!this.formsurveysAdd.valid) {
      return false;
    }

    // Si seleccionó "Escoger especialidades", debe tener al menos una seleccionada
    if (this.shortSurveysForm.select.value === 2) {
      return this.select_specialties.length > 0;
    }

    return true;
  }

  /**
   * Limpia el formulario completamente
   */
  resetForm() {
    this.formsurveysAdd.reset();
    this.select_specialties = [];
    this.search_specialties = [];
    this.search_specilties_text = '';
    this.error_selected = '';
    this.div_error = 0;
    this.div_text_error = '';
    this.submitted = false;
    this.preloader = false;
    this.isCreating = false;

    console.log('Formulario reseteado');
  }

  /**
   * Método de debug para revisar el estado del formulario
   */
  debugForm() {
    const debugInfo = {
      formValid: this.formsurveysAdd.valid,
      formValues: this.formsurveysAdd.value,
      formErrors: this.getFormErrors(),
      specialtiesSelected: this.select_specialties,
      selectValue: this.shortSurveysForm.select.value,
      isFormValid: this.isFormValid(),
      isCreating: this.isCreating,
      preloader: this.preloader
    };

    console.log('Estado del formulario:', debugInfo);
    return debugInfo;
  }

  /**
   * Valida un campo específico
   */
  isFieldInvalid(fieldName: string): boolean {
    const field = this.shortSurveysForm[fieldName];
    return field && field.invalid && (field.dirty || field.touched || this.submitted);
  }

  /**
   * Obtiene el mensaje de error para un campo
   */
  getFieldError(fieldName: string): string {
    const field = this.shortSurveysForm[fieldName];

    if (!field || !field.errors) {
      return '';
    }

    if (field.errors.required) {
      return 'Este campo es requerido';
    }

    if (field.errors.minlength) {
      return `Mínimo ${field.errors.minlength.requiredLength} caracteres`;
    }

    if (field.errors.maxlength) {
      return `Máximo ${field.errors.maxlength.requiredLength} caracteres`;
    }

    if (field.errors.pattern) {
      return 'Solo se permiten letras, números y signos de puntuación básicos';
    }

    if (field.errors.min) {
      return 'Debe seleccionar una opción válida';
    }

    return 'Campo inválido';
  }

  /**
   * Cuenta caracteres de un campo
   */
  getCharacterCount(fieldName: string): number {
    const field = this.shortSurveysForm[fieldName];
    return field.value.length || 0;
  }

  /**
   * Obtiene el máximo de caracteres para un campo
   */
  getMaxLength(fieldName: string): number {
    const maxLengths = {
      title: 150,
      description: 500
    };
    return maxLengths[fieldName] || 0;
  }

  /**
   * Verifica si se puede crear la encuesta
   */
  canCreateSurvey(): boolean {
    return this.isFormValid() && !this.isCreating && !this.preloader;
  }
}
